import { useState } from 'preact/hooks';
import { CurrentUserProfileIcon } from '@components/avatars/user-profile-icon';
import { ManualDom } from '@components/manual-dom';
import { useMinidoc } from '@components/minidoc';
import { cardMiddleware, defaultToolbarActions, minidocToolbar, placeholder } from 'minidoc-editor';
import { generateMediaCard, mediaMiddleware } from '@components/media-card';
import { embedHtmlCard } from '@components/raw-html-card';
import { toolbarDropdown } from '@components/toolbar-dropdown';
import { useIntl } from 'shared/intl/use-intl';
import { rpx } from 'client/lib/rpx-client';
import { AIP_COURSE_ID } from 'shared/mirasee-consts';
import { showError } from '@components/app-error';
import { EditorWrapper } from '@components/minidoc/minidoc-root';
import { IcoChevronDown, IcoClock, IcoX } from '@components/icons';
import { DateInput } from '@components/date-picker';
import { fmtFullTime } from 'shared/dateutil';
import { ALL_MEMBERS_ID, Mentionable } from '@course/components/discussions/mention-picker';
import type { CommunityCategories } from './types';

type Props = {
  onPostCreated: () => void;
  categories: CommunityCategories;
  canSchedule?: boolean;
};

export function PostComposer({ onPostCreated, categories, canSchedule }: Props) {
  const intl = useIntl();
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [createdPostId, setCreatedPostId] = useState<string | undefined>();
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined);
  const [showSchedulePicker, setShowSchedulePicker] = useState(false);

  const editor = useMinidoc({
    doc: '',
    middleware: () => [
      placeholder("What's on your mind?"),
      minidocToolbar([
        ...defaultToolbarActions.filter(
          (x) => x.id === 'bold' || x.id === 'italic' || x.id === 'link',
        ),
        toolbarDropdown({ intl, mediaOnly: true, showVimeo: true }),
      ]),
      cardMiddleware([generateMediaCard({}), embedHtmlCard]),
      mediaMiddleware(),
    ],
    onChange() {
      if (!isExpanded) {
        setIsExpanded(true);
      }
    },
  });

  async function submit() {
    const description = editor.serialize();
    if (!description.trim()) {
      return;
    }
    setIsSubmitting(true);
    try {
      const rawText = editor.root.textContent?.trim() || '';
      const prompt =
        rawText.length <= 80
          ? rawText || 'New post'
          : rawText.slice(0, 80).replace(/\s+\S*$/, '') + '…';
      const mentionEls = Array.from(editor.root.querySelectorAll<HTMLElement>('a[data-user-id]'));
      const mentions = [
        ...new Set(mentionEls.map((x) => x.dataset.userId || '').filter((x) => !!x)),
      ];
      const hasAllMention = mentions.includes(ALL_MEMBERS_ID);
      const result = await rpx.discussions.upsertDiscussion({
        courseId: AIP_COURSE_ID,
        prompt,
        description,
        categoryId,
        scheduledAt: scheduledAt?.toISOString(),
        mentions: hasAllMention ? [] : mentions,
        hasAllMention,
      });
      editor.root.innerHTML = '';
      setCategoryId(undefined);
      setScheduledAt(undefined);
      setShowSchedulePicker(false);
      setIsExpanded(false);
      setCreatedPostId(result.id);
      onPostCreated();
    } catch (err) {
      showError(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (createdPostId) {
    return (
      <div class="bg-indigo-50/60 dark:bg-gray-800/60 rounded-2xl p-4 mb-6">
        <div class="flex flex-col items-center gap-4 py-6 text-center">
          <div class="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              class="text-green-600 dark:text-green-400"
              style="width: 20px; height: 20px"
            >
              <path
                fill-rule="evenodd"
                d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            Thanks for sharing in the community!
          </p>
          <div class="flex items-center gap-3">
            <a
              href={`/courses/${AIP_COURSE_ID}/discussions/all/${createdPostId}`}
              class="inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 no-underline transition"
            >
              View your post &rarr;
            </a>
            <button
              class="inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
              onClick={() => setCreatedPostId(undefined)}
            >
              Create another post
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="bg-indigo-50/60 dark:bg-gray-800/60 rounded-2xl p-4 mb-6">
      <div class="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus-within:ring-2 focus-within:ring-indigo-400 focus-within:border-indigo-400 transition">
        <div class="flex gap-3 px-4 pt-3">
          <CurrentUserProfileIcon size="w-10 h-10 text-xs shrink-0 hidden sm:block" />
          <div class="flex-1">
            <div
              class="relative"
              onClick={() => {
                if (!isExpanded) {
                  setIsExpanded(true);
                }
                (editor.root as HTMLElement).focus();
              }}
            >
              <Mentionable courseId={AIP_COURSE_ID} editor={editor} />
              <EditorWrapper editor={editor} class="py-1 min-h-[48px] text-sm cursor-text" />
            </div>
          </div>
        </div>
        {isExpanded && showSchedulePicker && (
          <div class="px-3 pb-2">
            <div class="flex items-center gap-2">
              <DateInput
                includeTime
                min={new Date()}
                value={scheduledAt}
                placeholder={intl('Pick date and time')}
                class="text-sm"
                onChange={(dt) => setScheduledAt(dt)}
              />
              {scheduledAt && (
                <button
                  class="p-1 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setScheduledAt(undefined);
                    setShowSchedulePicker(false);
                  }}
                >
                  <IcoX class="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}
        <div class="border-t border-gray-100 dark:border-gray-600 px-3 py-2 flex flex-col gap-2">
          <ManualDom el={editor.toolbar.root} class="flex items-center gap-0.5" />
          <div class="flex items-center justify-between">
            <div class="relative inline-flex items-center">
              {categoryId && (
                <span
                  class="absolute left-2.5 w-2 h-2 rounded-full pointer-events-none"
                  style={{ backgroundColor: categories.find((c) => c.id === categoryId)?.color }}
                />
              )}
              <select
                class="appearance-none text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 border-none focus:ring-2 focus:ring-indigo-400 focus:outline-none cursor-pointer rounded-full py-2 pr-7 transition"
                style={{ paddingLeft: categoryId ? '1.25rem' : '0.625rem' }}
                value={categoryId || ''}
                onChange={(e: any) => setCategoryId(e.target.value || undefined)}
              >
                <option value="">No category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.title}
                  </option>
                ))}
              </select>
              <IcoChevronDown class="absolute right-2 w-3 h-3 text-gray-400 pointer-events-none" />
            </div>
            <div class="flex items-center gap-2">
              {canSchedule && isExpanded && (
                <button
                  class={`p-1.5 rounded-full transition ${
                    showSchedulePicker || scheduledAt
                      ? 'text-indigo-600 bg-indigo-50'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  onClick={() => setShowSchedulePicker((v) => !v)}
                  title={intl('Schedule post')}
                >
                  <IcoClock class="w-4 h-4" />
                </button>
              )}
              {scheduledAt && (
                <span class="text-xs text-indigo-600 hidden sm:inline">
                  {fmtFullTime(scheduledAt)}
                </span>
              )}
              <button
                class="inline-flex items-center justify-center rounded-full px-5 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 transition disabled:opacity-50"
                onClick={submit}
                disabled={isSubmitting}
              >
                {scheduledAt ? intl('Schedule') : intl('Post')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
